import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useLanguage } from "../context/LanguageContext";

import GroupPageShell from "../components/common/GroupPageShell";
import DivisionIntro from "../components/common/DivisionIntro";
import DivisionContact from "../components/common/DivisionContact";
import CodeOfPractice from "../components/Hamdan Transport/CodeOfPractice";
import Approach from "../components/Hamdan Transport/Approach";
import History from "../components/Hamdan Transport/Activities";
import Gallery from "../components/Hamdan Transport/Gallery";
import Safety from "../components/Hamdan Transport/Safety";
import Awards from "../components/Hamdan Transport/Awards";
import TrackingSystem from "../components/Hamdan Transport/TrackingSystem";
import Clients from "../components/common/DivisionClients";
import DivisionClientsStrip from "../components/common/DivisionClientsStrip";
import { buildClientLogoImages } from "../utils/clientLogos";
import DivisionServices from "../components/common/DivisionServices";
import DivisionBranches from "../components/common/DivisionBranches";

type TabId =
  | "about"
  | "services"
  | "branches"
  | "history"
  | "gallery"
  | "safety"
  | "awards"
  | "tracking"
  | "clients"
  | "contact";

const TABS: { id: TabId; labelKey: string }[] = [
  { id: "about", labelKey: "common.intro" },
  { id: "services", labelKey: "common.services" },
  { id: "branches", labelKey: "common.branches" },
  { id: "history", labelKey: "common.history" },
  { id: "gallery", labelKey: "common.gallery" },
  { id: "safety", labelKey: "common.qhse" },
  { id: "awards", labelKey: "common.awards" },
  { id: "tracking", labelKey: "common.tech" },
  { id: "clients", labelKey: "common.clients" },
  { id: "contact", labelKey: "common.contact" },
];

const isValidTab = (t: string | null): t is TabId =>
  !!t && (TABS as any).some((x: any) => x.id === t);

const HamdanTransport: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [content, setContent] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const { t, t_api } = useLanguage();

  // Initial tab from URL (?tab=...), fallback to 'about'
  const tabParam = searchParams.get("tab");
  const active: TabId = isValidTab(tabParam) ? tabParam : "about";

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const response = await fetch("/api/homepage/content");
        const data = await response.json();

        if (data.Transport) {
          const raw = data.Transport;

          // Helper to get image URL correctly
          const getImg = (val: string) => {
            if (!val) return null;
            if (val.startsWith('http')) return val;
            return `https://hamdanoman.com${val}`;
          };

          // Transform flat data to structured content for components
          const structured = {
            hero: {
              title: t_api(raw.banner_title, raw.banner_title_ar),
              subtitle: t_api(raw.banner_subtitle, raw.banner_subtitle_ar),
              banners: [
                { image: getImg(raw.banner_1) },
                { image: getImg(raw.banner_2) },
                { image: getImg(raw.banner_3) },
              ].filter(b => b.image)
            },
            about: {
              title: t_api(raw.about_title || "About Hamdan Transport", raw.about_title_ar),
              subtitle: t_api(raw.about_subtitle || "Excellence in Transport", raw.about_subtitle_ar),
              desc1: t_api(raw.about_desc?.split('\n\n')[0], raw.about_desc_ar?.split('\n\n')[0]),
              desc2: t_api(raw.about_desc?.split('\n\n')[1], raw.about_desc_ar?.split('\n\n')[1]),
              desc3: t_api(raw.about_desc?.split('\n\n')[2], raw.about_desc_ar?.split('\n\n')[2]),
              image: getImg(raw.about_image),
              points: [
                { title: t_api(raw.val_1_title, raw.val_1_title_ar), desc: t_api(raw.val_1_desc, raw.val_1_desc_ar), iconName: raw.val_1_icon },
                { title: t_api(raw.val_2_title, raw.val_2_title_ar), desc: t_api(raw.val_2_desc, raw.val_2_desc_ar), iconName: raw.val_2_icon },
                { title: t_api(raw.val_3_title, raw.val_3_title_ar), desc: t_api(raw.val_3_desc, raw.val_3_desc_ar), iconName: raw.val_3_icon },
              ].filter(p => p.title)
            },
            values: {
              title: t_api(raw.values_title, raw.values_title_ar),
              description: t_api(raw.values_desc, raw.values_desc_ar),
              items: [
                { title: t_api(raw.val_1_title, raw.val_1_title_ar), description: t_api(raw.val_1_desc, raw.val_1_desc_ar), iconName: raw.val_1_icon },
                { title: t_api(raw.val_2_title, raw.val_2_title_ar), description: t_api(raw.val_2_desc, raw.val_2_desc_ar), iconName: raw.val_2_icon },
                { title: t_api(raw.val_3_title, raw.val_3_title_ar), description: t_api(raw.val_3_desc, raw.val_3_desc_ar), iconName: raw.val_3_icon },
                { title: t_api(raw.val_4_title, raw.val_4_title_ar), description: t_api(raw.val_4_desc, raw.val_4_desc_ar), iconName: raw.val_4_icon },
              ].filter(i => i.title)
            },
            code: {
              title: t_api(raw.code_title, raw.code_title_ar),
              description: t_api(raw.code_desc, raw.code_desc_ar),
              footerDescription: t_api(raw.code_footer, raw.code_footer_ar),
              items: [
                { title: t_api(raw.code_1_title, raw.code_1_title_ar), description: t_api(raw.code_1_desc, raw.code_1_desc_ar), iconName: raw.code_1_icon || 'Trophy', color: 'text-yellow-600' },
                { title: t_api(raw.code_2_title, raw.code_2_title_ar), description: t_api(raw.code_2_desc, raw.code_2_desc_ar), iconName: raw.code_2_icon || 'Handshake', color: 'text-green-600' },
                { title: t_api(raw.code_3_title, raw.code_3_title_ar), description: t_api(raw.code_3_desc, raw.code_3_desc_ar), iconName: raw.code_3_icon || 'Heart', color: 'text-red-600' },
                { title: t_api(raw.code_4_title, raw.code_4_title_ar), description: t_api(raw.code_4_desc, raw.code_4_desc_ar), iconName: raw.code_4_icon || 'Eye', color: 'text-blue-600' },
              ].filter(i => i.title)
            },
            approach: {
              title: t_api(raw.approach_title, raw.approach_title_ar),
              intro: t_api(raw.approach_intro, raw.approach_intro_ar),
              footerText: t_api(raw.approach_footer, raw.approach_footer_ar),
              footerTagline: t_api(raw.approach_tagline, raw.approach_tagline_ar),
              items: [
                { title: t_api(raw.app_1_title, raw.app_1_title_ar), description: t_api(raw.app_1_desc, raw.app_1_desc_ar), iconName: raw.app_1_icon || 'Target', color: raw.app_1_color || 'blue' },
                { title: t_api(raw.app_2_title, raw.app_2_title_ar), description: t_api(raw.app_2_desc, raw.app_2_desc_ar), iconName: raw.app_2_icon || 'Users', color: raw.app_2_color || 'green' },
                { title: t_api(raw.app_3_title, raw.app_3_title_ar), description: t_api(raw.app_3_desc, raw.app_3_desc_ar), iconName: raw.app_3_icon || 'Zap', color: raw.app_3_color || 'purple' },
                { title: t_api(raw.app_4_title, raw.app_4_title_ar), description: t_api(raw.app_4_desc, raw.app_4_desc_ar), iconName: raw.app_4_icon || 'Award', color: raw.app_4_color || 'orange' },
                { title: t_api(raw.app_5_title, raw.app_5_title_ar), description: t_api(raw.app_5_desc, raw.app_5_desc_ar), iconName: raw.app_5_icon || 'Settings', color: raw.app_5_color || 'indigo' },
              ].filter(i => i.title)
            },
            history: {
              title: t_api(raw.history_title, raw.history_title_ar),
              desc: t_api(raw.history_subtitle, raw.history_subtitle_ar),
              items: [
                { title: t_api(raw.hist_1_title, raw.hist_1_title_ar), description: t_api(raw.hist_1_desc, raw.hist_1_desc_ar), iconName: raw.hist_1_icon, images: [getImg(raw.hist_1_img_1), getImg(raw.hist_1_img_2)].filter(Boolean) },
                { title: t_api(raw.hist_2_title, raw.hist_2_title_ar), description: t_api(raw.hist_2_desc, raw.hist_2_desc_ar), iconName: raw.hist_2_icon, images: [getImg(raw.hist_2_img_1), getImg(raw.hist_2_img_2)].filter(Boolean) },
                { title: t_api(raw.hist_3_title, raw.hist_3_title_ar), description: t_api(raw.hist_3_desc, raw.hist_3_desc_ar), iconName: raw.hist_3_icon, images: [getImg(raw.hist_3_img_1), getImg(raw.hist_3_img_2)].filter(Boolean) },
                { title: t_api(raw.hist_4_title, raw.hist_4_title_ar), description: t_api(raw.hist_4_desc, raw.hist_4_desc_ar), iconName: raw.hist_4_icon, images: [getImg(raw.hist_4_img_1), getImg(raw.hist_4_img_2)].filter(Boolean) },
                { title: t_api(raw.hist_5_title, raw.hist_5_title_ar), description: t_api(raw.hist_5_desc, raw.hist_5_desc_ar), iconName: raw.hist_5_icon, images: [getImg(raw.hist_5_img_1), getImg(raw.hist_5_img_2)].filter(Boolean) },
              ].filter(i => i.title)
            },
            gallery: {
              title: t_api(raw.gallery_title || "Hamdan Transport Gallery", raw.gallery_title_ar),
              images: Array.from({ length: 150 }, (_, i) => getImg(raw[`gal_${i + 1}`])).filter(Boolean)
            },
            safety: {
              title: t_api(raw.qhse_title, raw.qhse_title_ar),
              descTop: t_api(raw.qhse_desc_top, raw.qhse_desc_top_ar),
              descMid: t_api(raw.qhse_desc_mid, raw.qhse_desc_mid_ar),
              descBottom: t_api(raw.qhse_desc_bottom, raw.qhse_desc_bottom_ar),
              points: [
                raw.qhse_pt_1, raw.qhse_pt_2, raw.qhse_pt_3, raw.qhse_pt_4,
                raw.qhse_pt_5, raw.qhse_pt_6, raw.qhse_pt_7
              ].filter(Boolean).map((p, idx) => t_api(p, raw[`qhse_pt_${idx + 1}_ar`])),
              images: [
                getImg(raw.qhse_img_1), getImg(raw.qhse_img_2),
                getImg(raw.qhse_img_3), getImg(raw.qhse_img_4)
              ].filter(Boolean)
            },
            tracking: {
              title: t_api(raw.tech_title, raw.tech_title_ar),
              desc: t_api(raw.tech_desc, raw.tech_desc_ar),
              image: getImg(raw.tech_image),
              systems: [
                { iconName: raw.tech_sys_1_icon, title: t_api(raw.tech_sys_1_title, raw.tech_sys_1_title_ar), description: t_api(raw.tech_sys_1_desc, raw.tech_sys_1_desc_ar) },
                { iconName: raw.tech_sys_2_icon, title: t_api(raw.tech_sys_2_title, raw.tech_sys_2_title_ar), description: t_api(raw.tech_sys_2_desc, raw.tech_sys_2_desc_ar) },
                { iconName: raw.tech_sys_3_icon, title: t_api(raw.tech_sys_3_title, raw.tech_sys_3_title_ar), description: t_api(raw.tech_sys_3_desc, raw.tech_sys_3_desc_ar) },
                { iconName: raw.tech_sys_4_icon, title: t_api(raw.tech_sys_4_title, raw.tech_sys_4_title_ar), description: t_api(raw.tech_sys_4_desc, raw.tech_sys_4_desc_ar) },
              ].filter(s => s.title),
              benefits: [1, 2, 3].map(i => ({
                title: t_api(raw[`tech_bn_${i}_title`], raw[`tech_bn_${i}_title_ar`]),
                desc: t_api(raw[`tech_bn_${i}_desc`], raw[`tech_bn_${i}_desc_ar`])
              })).filter(b => b.title || b.desc),
              benefitTitle: t_api(raw.tech_benefit_title, raw.tech_benefit_title_ar),
              strategicLabel: t_api(raw.tech_strategic_label, raw.tech_strategic_label_ar)
            },
            awards: {
              title: t_api(raw.awards_title, raw.awards_title_ar),
              desc: t_api(raw.awards_desc, raw.awards_desc_ar),
              footer: t_api(raw.award_footer, raw.award_footer_ar),
              achievements: [
                { text: t_api(raw.award_pt_1_text, raw.award_pt_1_text_ar), iconName: raw.award_pt_1_icon || 'Shield' },
                { text: t_api(raw.award_pt_2_text, raw.award_pt_2_text_ar), iconName: raw.award_pt_2_icon || 'CheckCircle2' },
                { text: t_api(raw.award_pt_3_text, raw.award_pt_3_text_ar), iconName: raw.award_pt_3_icon || 'Star' },
                { text: t_api(raw.award_pt_4_text, raw.award_pt_4_text_ar), iconName: raw.award_pt_4_icon || 'Award' },
                { text: t_api(raw.award_pt_5_text, raw.award_pt_5_text_ar), iconName: raw.award_pt_5_icon || 'Award' },
                { text: t_api(raw.award_pt_6_text, raw.award_pt_6_text_ar), iconName: raw.award_pt_6_icon || 'Users' },
              ].filter(a => a.text),
              images: [
                getImg(raw.award_img_1), getImg(raw.award_img_2), getImg(raw.award_img_3), getImg(raw.award_img_4),
                getImg(raw.award_img_5),
              ].filter(Boolean)
            },
            clients: {
              title: t_api(raw.clients_title, raw.clients_title_ar),
              desc: t_api(raw.clients_desc, raw.clients_desc_ar),
              clientsText: t_api(raw.clients_list_text, raw.clients_list_text_ar),
              logoUrls: t_api(raw.clients_logo_urls, raw.clients_logo_urls_ar),
              logoImages: buildClientLogoImages(raw, "clients_list_text").map(getImg),
              divisionLabel: "Hamdan Transport Division",
              footer: t_api(raw.clients_footer, raw.clients_footer_ar),
              footerDesc: t_api(raw.clients_footer_desc, raw.clients_footer_desc_ar)
            },
            contact: {
              address: t_api(raw.contact_address, raw.contact_address_ar),
              phone: raw.contact_phone,
              fax: raw.contact_fax,
              email: raw.contact_email,
              branches: Array.from({ length: 40 }, (_, i) => i + 1)
                .reduce<Array<{ name: string; location: string; address?: string; phone?: string; fax?: string; email?: string; coordinates?: string }>>((acc, n) => {
                  const name = t_api(raw[`br_${n}_name`], raw[`br_${n}_name_ar`]);
                  if (name) {
                    acc.push({
                      name,
                      location: name,
                      address: t_api(raw[`br_${n}_addr`], raw[`br_${n}_addr_ar`]),
                      phone: raw[`br_${n}_phone`],
                      fax: raw[`br_${n}_fax`],
                      email: raw[`br_${n}_email`],
                      coordinates: raw[`br_${n}_coord`],
                    });
                  }
                  return acc;
                }, [])
            },
            services: {
              services_title: t_api(raw.services_title, raw.services_title_ar),
              svc_1_title: t_api(raw.svc_1_title, raw.svc_1_title_ar),
              svc_1_desc: t_api(raw.svc_1_desc, raw.svc_1_desc_ar),
              svc_1_image: getImg(raw.svc_1_image),
              svc_2_title: t_api(raw.svc_2_title, raw.svc_2_title_ar),
              svc_2_desc: t_api(raw.svc_2_desc, raw.svc_2_desc_ar),
              svc_2_image: getImg(raw.svc_2_image),
              svc_3_title: t_api(raw.svc_3_title, raw.svc_3_title_ar),
              svc_3_desc: t_api(raw.svc_3_desc, raw.svc_3_desc_ar),
              svc_3_image: getImg(raw.svc_3_image),
            }
          };
          setContent(structured);
        }
      } catch (err) {
        console.error("Failed to fetch transport content:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchContent();
  }, []);

  const setActive = (next: TabId) => {
    const sp = new URLSearchParams(searchParams);
    sp.set("tab", next);
    setSearchParams(sp, { replace: true });
  };

  if (loading) return (
    <div className="min-h-screen bg-white flex flex-col items-center justify-center">
      <div className="w-12 h-12 border-4 border-red-600 border-t-transparent rounded-full animate-spin mb-4"></div>
      <p className="text-gray-500 font-medium font-sans">{t('common.loading')}</p>
    </div>
  );

  const renderSection = () => {
    switch (active) {
      case "about":
        return (
          <>
            <DivisionIntro content={{
              kicker: "Hamdan Group - Oman",
              title: content?.about?.title || "Hamdan Transport Division",
              desc: [content?.about?.desc1, content?.about?.desc2, content?.about?.desc3].filter(Boolean).join("\n\n"),
              image: content?.about?.image || "/about.jpeg",
              imageFit: "cover",
              imageBadgeTitle: "Transport Division",
              imageLabel: content?.about?.title || "Hamdan Transport",
              noteTitle: content?.about?.subtitle || "Transport Excellence",
              noteDesc: "One of the premier transport service providers in the Sultanate of Oman.",
              panelTitle: "INTRODUCTION",
              capabilitiesTitle: "Transport Capabilities",
              capabilitiesMeta: "Fleet - Safety - Logistics",
              brandLogo: "/Hamdan-Transport-logo.jpg",
              brandDesc: "A transport division focused on safe movement, operational control, and dependable logistics support.",
              brandTags: ["Transport", "Logistics", "Oman"],
              points: (content?.about?.points?.length ? content.about.points : content?.values?.items || []).slice(0, 3),
            }} />
            <DivisionClientsStrip
              title={content?.clients?.title}
              desc={content?.clients?.desc}
              clientsText={content?.clients?.clientsText}
              logoUrls={content?.clients?.logoUrls}
              logoImages={content?.clients?.logoImages}
            />
            <CodeOfPractice content={content?.code} />
            <Approach content={content?.approach} />
          </>
        );
      case "history":
        return <History content={content?.history} />;
      case "gallery":
        return <Gallery content={content?.gallery} />;
      case "services":
        return <DivisionServices content={content?.services} />;
      case "branches":
        return (
          <DivisionBranches
            divisionLabel="Hamdan Transport"
            title="Branch Network"
            subtitle="Transport offices and operational branch locations across Oman."
            branches={content?.contact?.branches || []}
          />
        );
      case "safety":
        return <Safety content={content?.safety} />;
      case "awards":
        return <Awards content={content?.awards} />;
      case "tracking":
        return <TrackingSystem content={content?.tracking} />;
      case "clients":
        return <Clients content={content?.clients} />;
      case "contact": {
        const defaultMainContact = {
          location: "Hamdan Transport Division",
          address: content?.contact?.address,
          phone: content?.contact?.phone,
          fax: content?.contact?.fax,
          email: content?.contact?.email,
        };
        const contactInfoList = content?.contact?.branches?.length
          ? content.contact.branches
          : [defaultMainContact];

        return <DivisionContact content={{
          title: "Send Us Email",
          infoTitle: "Get in Touch",
          infoDesc: "We appreciate your interest in Hamdan Transport Division, and we want to make it as easy as possible for you to get your queries answered.",
          division: "Transport",
          contactInfo: contactInfoList,
          supportEmail: content?.contact?.email,
        }} />;
      }
    }
  };

  return (
    <GroupPageShell
      heroContent={content?.hero}
      tabs={TABS.map((tab) => ({ id: tab.id, label: t(tab.labelKey) }))}
      activeTab={active}
      onTabChange={(tabId) => setActive(tabId as TabId)}
    >
      {renderSection()}
    </GroupPageShell>
  );
};

export default HamdanTransport;
